import React, { useState, useEffect, useRef, useMemo } from 'react';
import { User, ChevronDown, Edit, Trash2, X, Loader, Calendar } from 'lucide-react';
import { GenericListView, Banner, ConfirmDialog, useToast } from '../common';
import { FormPageLayout } from '../common';
import type { ActionButtonConfig } from '../common/FormPageLayout';
import { fetchDemographics, updateDemographics } from '../../services/patientmr/demographics.service';
import { DemographicsData, DemographicsFormData } from '../../types/patientmr/demographics';
import type { AddressVerification, Allergy } from '../../dtos/patientmr/demographics.dto';
import type { HospitalizationInput } from '../../dtos/patientmr/hospitalization.dto';
import { fetchPatientById } from '../../services/admin/patient.service';
import { TEXAS_COUNTIES } from '../../constants/texas-counties';
import { notifyPatientChartUpdated } from '../../constants/navigationEvents';
import { useHasAnyPermission } from '../../hooks/usePermission';
import { useFeature } from '../../hooks/useFeature';
import GuardiansSection from './demographics/GuardiansSection';
import AllergiesSection, { AllergiesValue, validateAllergies } from './demographics/AllergiesSection';
import DiagnosisList from './demographics/DiagnosisList';
import HospitalizationDialog, { HospitalizationFormValue } from './demographics/HospitalizationDialog';

// Declare that we're extending the existing DemographicsData interface
declare module '../../types/patientmr/demographics' {
  interface DemographicsData {
    pastHospitalizations?: Hospitalization[];
  }
}

// Helper function to safely access hospitalization properties regardless of format
const getHospitalizationData = (hosp: any): {
  admitDate: string;
  dischargeDate: string;
  id: string;
  description: string;
  days: number;
} => {
  if (!hosp || typeof hosp !== 'object') {
    return {
      admitDate: '',
      dischargeDate: '',
      id: '',
      description: '',
      days: 0
    };
  }

  const admitDate = hosp.admissionDate || hosp.admitDate || '';
  const dischargeDate = hosp.dischargeDate || '';
  const id = hosp.id || hosp._id || '';
  const description = [hosp.reason, hosp.facility, hosp.notes || hosp.description].filter(Boolean).join(' — ');

  // Calculate days safely handling invalid dates
  let days = 0;
  if (admitDate && dischargeDate) {
    try {
      const admit = new Date(admitDate);
      const discharge = new Date(dischargeDate);

      if (!isNaN(admit.getTime()) && !isNaN(discharge.getTime())) {
        days = Math.ceil((discharge.getTime() - admit.getTime()) / (1000 * 60 * 60 * 24));
        // Ensure days is at least 0 (for same-day discharge)
        days = Math.max(0, days);
      }
    } catch (err) {
      console.error('Error calculating days between dates:', err);
    }
  }

  // Phase 6: the server computes lengthOfStayDays on save; unsaved (temp-) rows use the local estimate
  if (typeof hosp.lengthOfStayDays === 'number') {
    days = hosp.lengthOfStayDays;
  }

  return { admitDate, dischargeDate, id, description, days };
};

/** Map a stored/local hospitalization row to the §8 save contract (no extra keys). */
const toHospitalizationInput = (hosp: any): HospitalizationInput => {
  const row: HospitalizationInput = {
    admitDate: hosp.admitDate || hosp.admissionDate || '',
    dischargeDate: hosp.dischargeDate || '',
  };
  if (hosp.id) row.id = hosp.id;
  if (hosp.reason) row.reason = hosp.reason;
  if (hosp.facility) row.facility = hosp.facility;
  const notes = hosp.notes || hosp.description;
  if (notes) row.notes = notes;
  return row;
};

// Helper to format date as mm/dd/yyyy
const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

interface DemographicsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string; // Add patientId prop
  /** INACTIVE (discharged) patients are read-only (DSC-07) */
  readOnly?: boolean;
}

const Demographics = ({ expanded, toggleExpanded, patientId = '12345', readOnly = false }: DemographicsProps) => {
  const { showToast } = useToast();
  const uspsAddressVerifyEnabled = useFeature('uspsAddressVerify');
  // Permission checks for demographics
  const canReadDemographics = useHasAnyPermission([
    'patientmr:phi:demographics:read' as any,
    'patientmr:phi:demographics:readall' as any
  ]);

  const canUpdateDemographics = useHasAnyPermission([
    'patientmr:phi:demographics:update' as any,
    'patientmr:phi:demographics:updateall' as any
  ]);

  // Read-only view: has read but no update permission, or the patient is discharged
  const isReadOnlyView = readOnly || (canReadDemographics && !canUpdateDemographics);
  const canSaveDemographics = canUpdateDemographics && !readOnly;

  // State for demographics data
  const [demographicsData, setDemographicsData] = useState<DemographicsData | null>(null);
  // Q5 (Phase 7): with coverage records, the Medicaid ID / MBI come from Insurance and Coverage
  const coverageOwnsIds = (demographicsData?.insuranceSummary?.count ?? 0) > 0;

  // Add state to track the original demographics data (before user edits)
  const [originalDemographicsData, setOriginalDemographicsData] = useState<DemographicsData | null>(null);

  // Add state for patient MRN
  const [patientMRN, setPatientMRN] = useState<string>('');

  // State for loading and errors
  const [isLoadingDemographics, setIsLoadingDemographics] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [demographicsError, setDemographicsError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  /** Inline allergy validation shows after the first save attempt (PAT-04) */
  const [showAllergyError, setShowAllergyError] = useState(false);
  /** PAT-01 USPS result after a save (non-blocking) */
  const [addressVerification, setAddressVerification] = useState<AddressVerification | null>(null);

  // Refs for deduplication
  const hasLoadedDemographicsRef = useRef<string | null>(null);
  
  // Fetch demographics when component mounts or patientId changes
  useEffect(() => {
    if (!expanded || !patientId) {
      return;
    }

    // Skip if already loaded for this patient
    if (hasLoadedDemographicsRef.current === patientId) {
      return;
    }

    hasLoadedDemographicsRef.current = patientId;
    loadDemographics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, patientId]); // loadDemographics intentionally excluded to avoid circular dependency

  // Reset demographics ref when section is collapsed
  useEffect(() => {
    if (!expanded) {
      hasLoadedDemographicsRef.current = null;
    }
  }, [expanded]);

  // Helper function to convert ISO date format to YYYY-MM-DD format for date inputs
  const convertToDateInputFormat = (isoDate: string): string => {
    try {
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
        return isoDate;
      }
      
      // Parse ISO date and extract YYYY-MM-DD
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) {
        return isoDate;
      }
      
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('[Demographics] Error converting date:', error);
      return isoDate;
    }
  };

  // Load demographics data
  const loadDemographics = async () => {
    setIsLoadingDemographics(true);
    setDemographicsError('');
    try {
      const data = await fetchDemographics(patientId);
      
      // Normalize insurance info field names FIRST (backend uses medicaidID/medicareID, frontend uses medicaidId/medicareId)
      if (data.insuranceInfo) {
        // Map backend field names to frontend field names
        if ((data.insuranceInfo as any).medicaidID && !(data.insuranceInfo as any).medicaidId) {
          (data.insuranceInfo as any).medicaidId = (data.insuranceInfo as any).medicaidID;
        }
        if ((data.insuranceInfo as any).medicareID && !(data.insuranceInfo as any).medicareId) {
          (data.insuranceInfo as any).medicareId = (data.insuranceInfo as any).medicareID;
        }
      }
      
      // Check if required fields are missing from demographics
      const missingRequiredFields = !data?.firstName || !data?.lastName || !data?.dateOfBirth || !data?.gender;
      
      if (missingRequiredFields) {
        try {
          // Fetch patient data from patient service
          const patientData = await fetchPatientById(patientId);
          
          // Set MRN from patient data
          if (patientData.mrn) {
            setPatientMRN(patientData.mrn);
          }
          
          // Fill in missing fields from patient service
          if (!data.firstName && patientData.firstName) {
            data.firstName = patientData.firstName;
          }
          if (!data.lastName && patientData.lastName) {
            data.lastName = patientData.lastName;
          }
          if (!data.dateOfBirth && patientData.dob) {
            // Convert date format from ISO to YYYY-MM-DD
            data.dateOfBirth = convertToDateInputFormat(patientData.dob);
          }
          if (!data.gender && patientData.gender) {
            data.gender = patientData.gender;
          }
          
        } catch {
          console.error('[Demographics] Error fetching patient record');
          // Continue with demographics data even if patient fetch fails
        }
      } else {
        // Even if demographics has all required fields, still fetch MRN from patient service
        try {
          const patientData = await fetchPatientById(patientId);
          if (patientData.mrn) {
            setPatientMRN(patientData.mrn);
          }
        } catch {
          console.error('[Demographics] Error fetching patient MRN');
        }
      }
      
      setDemographicsData(data);
      setOriginalDemographicsData(JSON.parse(JSON.stringify(data))); // Deep copy for change detection
    } catch (err) {
      setDemographicsError('Failed to load patient demographics');
      console.error('Error loading demographics:', err);
      // Reset ref on error to allow retry
      hasLoadedDemographicsRef.current = null;
    } finally {
      setIsLoadingDemographics(false);
    }
  };

  // State for hospitalizations list expansion
  const [hospitalizationsExpanded, setHospitalizationsExpanded] = useState(true);

  // Add/edit hospitalization dialog (local only; saved with "Save Patient Data")
  const [hospitalizationDialog, setHospitalizationDialog] = useState<{ editingId: string | null; initial: HospitalizationFormValue | null } | null>(null);
  const [hospitalizationToDelete, setHospitalizationToDelete] = useState<string | null>(null);

  // Update the state for insurance holder to match backend data structure
  const [isPrimaryHolder, setIsPrimaryHolder] = useState(true);
  const [policyholder, setPolicyholder] = useState({
    fullName: '',
    dob: ''
  });  // Populate form fields when demographics data is loaded
  useEffect(() => {
    if (demographicsData) {
      // Update the insurance holder state to match the current demographics data
      if (demographicsData.insuranceInfo) {
        // Make sure we're using a boolean value for patientIsPrimaryPolicyHolder
        setIsPrimaryHolder(demographicsData.insuranceInfo.patientIsPrimaryPolicyHolder === true);
      }

      // Manually update form fields to ensure they're populated
      const firstNameField = document.getElementById('firstName') as HTMLInputElement;
      if (firstNameField) firstNameField.value = demographicsData.firstName || '';

      const lastNameField = document.getElementById('lastName') as HTMLInputElement;
      if (lastNameField) lastNameField.value = demographicsData.lastName || '';

      const dobField = document.getElementById('dob') as HTMLInputElement;
      if (dobField) dobField.value = demographicsData.dateOfBirth || '';

      const genderField = document.getElementById('gender') as HTMLInputElement;
      if (genderField) genderField.value = demographicsData.gender || '';

      const phoneField = document.getElementById('phone') as HTMLInputElement;
      if (phoneField) phoneField.value = demographicsData.phoneNumber || '';

      const emailField = document.getElementById('email') as HTMLInputElement;
      if (emailField) emailField.value = demographicsData.email || '';

      // Update address fields
      if (demographicsData.address) {
        const streetField = document.getElementById('streetName') as HTMLInputElement;
        if (streetField) streetField.value = demographicsData.address.street1 || '';

        const cityField = document.getElementById('city') as HTMLInputElement;
        if (cityField) cityField.value = demographicsData.address.city || '';

        const stateField = document.getElementById('state') as HTMLInputElement;
        if (stateField) stateField.value = demographicsData.address.state || '';

        const zipField = document.getElementById('zip') as HTMLInputElement;
        if (zipField) zipField.value = demographicsData.address.zipCode || '';
      }

      // Update emergency contact fields
      if (demographicsData.emergencyContact) {
        const ecNameField = document.getElementById('emergencyContactName') as HTMLInputElement;
        if (ecNameField) ecNameField.value = demographicsData.emergencyContact.name || '';

        const ecPhoneField = document.getElementById('emergencyContactPhone') as HTMLInputElement;
        if (ecPhoneField) ecPhoneField.value = demographicsData.emergencyContact.phoneNumber || '';
      }

      // Update insurance fields
      if (demographicsData.insuranceInfo) {
        const providerField = document.getElementById('primaryInsuranceProvider') as HTMLInputElement;
        if (providerField) providerField.value = demographicsData.insuranceInfo.provider || '';

        const policyField = document.getElementById('primaryInsuranceID') as HTMLInputElement;
        if (policyField) policyField.value = demographicsData.insuranceInfo.policyNumber || '';        // Set primary holder state based on backend data
        setIsPrimaryHolder(demographicsData.insuranceInfo.patientIsPrimaryPolicyHolder === true);

        // Set policyholder information if available
        if (demographicsData.insuranceInfo.policyHolderName || demographicsData.insuranceInfo.policyHolderDob) {
          setPolicyholder({
            fullName: demographicsData.insuranceInfo.policyHolderName || '',
            dob: demographicsData.insuranceInfo.policyHolderDob || ''
          });
        }
      }
    }
  }, [demographicsData]);

  const openAddHospitalization = () => setHospitalizationDialog({ editingId: null, initial: null });

  const openEditHospitalization = (hospitalizationId: string) => {
    const hosp = demographicsData?.pastHospitalizations?.find((h) => h.id === hospitalizationId);
    if (!hosp) return;
    setHospitalizationDialog({
      editingId: hospitalizationId,
      initial: {
        admitDate: hosp.admitDate || hosp.admissionDate || '',
        dischargeDate: hosp.dischargeDate || '',
        reason: hosp.reason || '',
        facility: hosp.facility || '',
        notes: hosp.notes || hosp.description || '',
      },
    });
  };

  /** Add or edit a row locally. New rows keep a temp- id; the server assigns the UUID on save. */
  const saveHospitalizationLocally = (value: HospitalizationFormValue) => {
    const editingId = hospitalizationDialog?.editingId ?? null;
    setDemographicsData((prev) => {
      if (!prev) return prev;
      const rows = prev.pastHospitalizations || [];
      if (editingId) {
        return {
          ...prev,
          pastHospitalizations: rows.map((h) =>
            h.id === editingId
              ? { ...h, ...value, admissionDate: undefined, description: undefined, lengthOfStayDays: undefined }
              : h
          ),
        };
      }
      return {
        ...prev,
        pastHospitalizations: [...rows, { id: `temp-${Date.now()}`, patientId: prev.patientId || patientId, ...value }],
      };
    });
    setHospitalizationDialog(null);
  };

  /** Delete locally; the removal is saved with "Save Patient Data". */
  const confirmDeleteHospitalization = () => {
    const targetId = hospitalizationToDelete;
    setHospitalizationToDelete(null);
    if (!targetId) return;
    setDemographicsData((prev) =>
      prev ? { ...prev, pastHospitalizations: (prev.pastHospitalizations || []).filter((h) => h.id !== targetId) } : prev
    );
  };

  const allergyValue: AllergiesValue = {
    allergies: (demographicsData?.allergies || []) as Allergy[],
    allergiesNoneKnown: demographicsData?.allergiesNoneKnown === true,
  };

// Handle demographics form submission
  const handleDemographicsSubmit = async () => {
    const allergyError = validateAllergies(allergyValue);
    if (allergyError) {
      setShowAllergyError(true);
      showToast(allergyError, { variant: 'error' });
      document.getElementById('demographics-allergies')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    try {
      setIsSubmitting(true);
      const changedFields: Record<string, any> = {};
      // Helper function to check if a field has changed from the ORIGINAL data
      const hasFieldChanged = (elementId: string, originalValue: any): boolean => {
        const element = document.getElementById(elementId) as HTMLInputElement | HTMLTextAreaElement | null;
        if (!element || typeof element.value !== 'string') return false;
        const formValue = element.value;
        const origVal = originalValue === undefined || originalValue === null ? '' : originalValue;
        return formValue !== origVal;
      };
      // Basic identity fields - compare with originalDemographicsData instead of the current state
      if (hasFieldChanged('firstName', originalDemographicsData?.firstName)) changedFields.firstName = true;
      if (hasFieldChanged('lastName', originalDemographicsData?.lastName)) changedFields.lastName = true;
      if (hasFieldChanged('dob', originalDemographicsData?.dateOfBirth)) changedFields.dateOfBirth = true;
      if (hasFieldChanged('gender', originalDemographicsData?.gender)) changedFields.gender = true;
      // Contact information
      const phoneChanged = hasFieldChanged('phone', originalDemographicsData?.phoneNumber);
      if (phoneChanged) changedFields.phoneNumber = true;
      const emailChanged = hasFieldChanged('email', originalDemographicsData?.email);
      if (emailChanged) changedFields.email = true;
      // Address fields
      const streetChanged = hasFieldChanged('streetName', originalDemographicsData?.address?.street1);
      const cityChanged = hasFieldChanged('city', originalDemographicsData?.address?.city);
      const stateChanged = hasFieldChanged('state', originalDemographicsData?.address?.state);
      const zipChanged = hasFieldChanged('zip', originalDemographicsData?.address?.zipCode);
      if (streetChanged) changedFields.street = true;
      if (cityChanged) changedFields.city = true;
      if (stateChanged) changedFields.state = true;
      if (zipChanged) changedFields.zip = true;
      if (streetChanged || cityChanged || stateChanged || zipChanged) {
        changedFields.address = true;
      }
      // Emergency contact
      const ecNameChanged = hasFieldChanged('emergencyContactName', originalDemographicsData?.emergencyContact?.name);
      const ecPhoneChanged = hasFieldChanged('emergencyContactPhone', originalDemographicsData?.emergencyContact?.phoneNumber);

      if (ecNameChanged) changedFields.ecName = true;
      if (ecPhoneChanged) changedFields.ecPhone = true;

      // If any emergency contact field changed, mark the entire emergencyContact as changed  
      if (ecNameChanged || ecPhoneChanged) {
        changedFields.emergencyContact = true;
      }

      // Insurance
      const providerChanged = hasFieldChanged('primaryInsuranceProvider', originalDemographicsData?.insuranceInfo?.provider);
      const policyIdChanged = hasFieldChanged('primaryInsuranceID', originalDemographicsData?.insuranceInfo?.policyNumber);
      const medicareIdChanged = hasFieldChanged('medicareId', originalDemographicsData?.insuranceInfo?.medicareId);
      const medicaidIdChanged = hasFieldChanged('medicaidId', originalDemographicsData?.insuranceInfo?.medicaidId);
      const mcoNameChanged = hasFieldChanged('mcoName', originalDemographicsData?.insuranceInfo?.mcoName);
      const mcoMemberIdChanged = hasFieldChanged('mcoMemberId', originalDemographicsData?.insuranceInfo?.mcoMemberId);

      if (providerChanged) changedFields.provider = true;
      if (policyIdChanged) changedFields.policyNumber = true;
      if (medicareIdChanged) changedFields.medicareId = true;
      if (medicaidIdChanged) changedFields.medicaidId = true;
      if (mcoNameChanged) changedFields.mcoName = true;
      if (mcoMemberIdChanged) changedFields.mcoMemberId = true;      // Check if any insurance fields have changed, especially policy holder status
      const isPrimaryInsuranceHolder = demographicsData?.insuranceInfo?.patientIsPrimaryPolicyHolder === true;
      const isPolicyHolderChanged = originalDemographicsData?.insuranceInfo?.patientIsPrimaryPolicyHolder !== isPrimaryInsuranceHolder;
      const policyHolderNameChanged = hasFieldChanged('policyholderFullName', originalDemographicsData?.insuranceInfo?.policyHolderName);
      const policyHolderDobChanged = hasFieldChanged('policyholderDOB', originalDemographicsData?.insuranceInfo?.policyHolderDob);
      
      // Mark individual fields as changed
      if (isPolicyHolderChanged) changedFields.patientIsPrimaryPolicyHolder = true;
      if (policyHolderNameChanged) changedFields.policyHolderName = true;
      if (policyHolderDobChanged) changedFields.policyHolderDob = true;
      
      // If any insurance field changed, mark the entire insuranceInfo as changed
      if (providerChanged || policyIdChanged || isPolicyHolderChanged || 
          policyHolderNameChanged || policyHolderDobChanged || medicareIdChanged || 
          medicaidIdChanged || mcoNameChanged || mcoMemberIdChanged) {
        changedFields.insuranceInfo = true;
      }

      // Medical info (guardians now live in the legal-guardians table, PAT-02)
      const pcpNameChanged = hasFieldChanged('pcpName', originalDemographicsData?.pcpName);
      if (pcpNameChanged) changedFields.pcpName = true;

      const pcpPhoneChanged = hasFieldChanged('pcpPhone', originalDemographicsData?.pcpPhone);
      if (pcpPhoneChanged) changedFields.pcpPhone = true;

      const countyOfResidenceChanged = hasFieldChanged('countyOfResidence', originalDemographicsData?.countyOfResidence);
      if (countyOfResidenceChanged) changedFields.countyOfResidence = true;

      const datePhysicianLastSawClientChanged = hasFieldChanged('datePhysicianLastSawClient', originalDemographicsData?.datePhysicianLastSawClient);
      if (datePhysicianLastSawClientChanged) changedFields.datePhysicianLastSawClient = true;

      const birthHistoryChanged = hasFieldChanged('birthHistory', originalDemographicsData?.birthHistory);
      if (birthHistoryChanged) changedFields.birthHistory = true;

      const medicalEquipmentsChanged = hasFieldChanged('medicalEquipments', originalDemographicsData?.medicalEquipments);
      if (medicalEquipmentsChanged) changedFields.medicalEquipments = true;



      const precautionsChanged = hasFieldChanged('precautions', originalDemographicsData?.precautions);
      if (precautionsChanged) changedFields.precautions = true;
      // Create a form data object that includes all required identification fields
      // Always include required fields for backend
      const getInputValue = (id: string, fallback?: string): string => {
        const el = document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
        if (el && typeof el.value === 'string') return el.value;
        if (typeof fallback === 'string') return fallback;
        return '';
      };
      const formData: DemographicsFormData = {
        firstName: getInputValue('firstName', demographicsData?.firstName || ''),
        lastName: getInputValue('lastName', demographicsData?.lastName || ''),
        dateOfBirth: getInputValue('dob', demographicsData?.dateOfBirth || ''),
        gender: getInputValue('gender', demographicsData?.gender || 'Other'),
        address: demographicsData?.address || { street1: '', city: '', state: '', zipCode: '', country: 'USA' },
        emergencyContact: demographicsData?.emergencyContact || { name: '', phoneNumber: '' },
        insuranceInfo: demographicsData?.insuranceInfo || { provider: '', policyNumber: '' },
        phoneNumber: getInputValue('phone', demographicsData?.phoneNumber || ''),
      };
      // We already included all required identity fields above, no need to set them again
      // Just track which ones have changed in changedFields
      // Gender already included in the formData object

      // Only include email field if it's been modified
      if (emailChanged) {
        formData.email = getInputValue('email', demographicsData?.email || '');
      }

      // Address - only include if any address field changed
      if (streetChanged || cityChanged || stateChanged || zipChanged) {
        formData.address = {
          street1: getInputValue('streetName', demographicsData?.address?.street1 || ''),
          city: getInputValue('city', demographicsData?.address?.city || ''),
          state: getInputValue('state', demographicsData?.address?.state || ''),
          zipCode: getInputValue('zip', demographicsData?.address?.zipCode || ''),
          country: 'USA'
        };
        changedFields.address = true;
      }

      // Emergency contact - only include if any contact field changed
      if (ecNameChanged || ecPhoneChanged) {
        formData.emergencyContact = {
          name: getInputValue('emergencyContactName', demographicsData?.emergencyContact?.name || ''),
          relationship: demographicsData?.emergencyContact?.relationship || '',
          phoneNumber: getInputValue('emergencyContactPhone', demographicsData?.emergencyContact?.phoneNumber || '')
        };
        changedFields.emergencyContact = true;
      }
      if (providerChanged || policyIdChanged || 
          'patientIsPrimaryPolicyHolder' in changedFields ||
          'policyHolderName' in changedFields ||
          'policyHolderDob' in changedFields) {
        
        const isPrimaryInsuranceHolder = demographicsData?.insuranceInfo?.patientIsPrimaryPolicyHolder === true;
        
        formData.insuranceInfo = {
          provider: getInputValue('primaryInsuranceProvider', demographicsData?.insuranceInfo?.provider || ''),
          policyNumber: getInputValue('primaryInsurancePolicyNumber', demographicsData?.insuranceInfo?.policyNumber || ''),
          groupNumber: demographicsData?.insuranceInfo?.groupNumber || '',
          effectiveDate: demographicsData?.insuranceInfo?.effectiveDate || '',
          expirationDate: demographicsData?.insuranceInfo?.expirationDate || '',
          patientIsPrimaryPolicyHolder: isPrimaryInsuranceHolder,
          // Only include policyholder details if patient is not the primary holder
          policyHolderName: !isPrimaryInsuranceHolder ? getInputValue('policyholderName', '') : '',
          policyHolderDob: !isPrimaryInsuranceHolder ? getInputValue('policyholderDob', '') : '',
          // Texas Medicaid specific fields - send both camelCase and backend format
          medicaidId: getInputValue('medicaidId', demographicsData?.insuranceInfo?.medicaidId || ''),
          medicaidID: getInputValue('medicaidId', demographicsData?.insuranceInfo?.medicaidID || ''),
          mcoName: getInputValue('mcoName', demographicsData?.insuranceInfo?.mcoName || ''),
          mcoMemberId: getInputValue('mcoMemberId', demographicsData?.insuranceInfo?.mcoMemberId || ''),
          medicareId: getInputValue('medicareId', demographicsData?.insuranceInfo?.medicareId || ''),
          medicareID: getInputValue('medicareId', demographicsData?.insuranceInfo?.medicareID || '')
        } as any;
        changedFields.insuranceInfo = true;
      }

      // Medical fields - only include changed values
      if (pcpNameChanged) {
        formData.pcpName = getInputValue('pcpName', demographicsData?.pcpName || '');
        changedFields.pcpName = true;
      }

      if (pcpPhoneChanged) {
        formData.pcpPhone = getInputValue('pcpPhone', demographicsData?.pcpPhone || '');
        changedFields.pcpPhone = true;
      }

      if (countyOfResidenceChanged) {
        formData.countyOfResidence = getInputValue('countyOfResidence', demographicsData?.countyOfResidence || '');
        changedFields.countyOfResidence = true;
      }
      if (datePhysicianLastSawClientChanged) {
        formData.datePhysicianLastSawClient = getInputValue('datePhysicianLastSawClient', demographicsData?.datePhysicianLastSawClient || '');
        changedFields.datePhysicianLastSawClient = true;
      }

      if (birthHistoryChanged) {
        formData.birthHistory = getInputValue('birthHistory', demographicsData?.birthHistory || '');
        changedFields.birthHistory = true;
      }

      if (medicalEquipmentsChanged) {
        formData.medicalEquipments = getInputValue('medicalEquipments', demographicsData?.medicalEquipments || '');
        changedFields.medicalEquipments = true;
      }



      if (precautionsChanged) {
        formData.precautions = getInputValue('precautions', demographicsData?.precautions || '');
        changedFields.precautions = true;
      }

      // Medicare and Medicaid IDs are now handled through insuranceInfo updates
      // No need for separate top-level field updates

      // If patient is not the primary insurance holder, add policyholder info
      if (!isPrimaryHolder && policyholder.fullName && policyholder.dob) {
        // Handle policyholder data if needed
        changedFields.policyholder = true;
      }

      // Hospitalizations always go in the §8 row shape (id/admit/discharge/reason/facility/notes).
      // temp- ids are replaced by the server; an empty list clears the last row.
      formData.pastHospitalizations = (demographicsData?.pastHospitalizations || []).map(toHospitalizationInput);

      // Allergies (PAT-04): a list or an explicit "none known", never both
      formData.allergiesNoneKnown = allergyValue.allergiesNoneKnown;
      formData.allergies = allergyValue.allergiesNoneKnown ? [] : allergyValue.allergies;

      // Merge form data with existing demographics to preserve all backend fields
      // This ensures we send a complete object with all required backend fields
      const completeFormData: DemographicsFormData = {
        // Start with existing demographics data to preserve backend fields
        ...demographicsData,
        // Override with form data
        ...formData
      };

      // Submit to API
      const saved = await updateDemographics(patientId, completeFormData);
      const verification = saved?.addressVerification;
      setAddressVerification(
        verification && (verification.status === 'SUGGESTED' || verification.status === 'NOT_FOUND') ? verification : null
      );

      // Show success message
      setSuccessMessage('Demographics information updated successfully');
      setDemographicsError(''); // Clear any previous errors
      setShowAllergyError(false);
      notifyPatientChartUpdated(patientId);

      // Reload demographics data to reflect changes
      await loadDemographics();
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      showToast(
        error instanceof Error && error.message ? error.message : 'Failed to update demographics information. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Memoize 254 Texas county options to avoid recreating on every render
  const texasCountyOptions = useMemo(() =>
    TEXAS_COUNTIES.map(county => (
      <option key={county} value={county}>{county}</option>
    )), []);

  return (
    <div className="mb-0">
      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;  
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
      <div className="bg-white md:rounded md:shadow overflow-hidden md:border-b md:border-gray-200 mb-0 md:mb-2">
        <div
          className="py-4 px-4 md:p-4 flex items-center justify-between cursor-pointer bg-slate-50 hover:bg-slate-100 active:bg-slate-200 md:bg-white md:hover:bg-slate-50 md:active:bg-slate-100 transition-colors border-b border-slate-200 md:border-gray-200"
          onClick={toggleExpanded}
        >
          <h2 className="text-lg font-semibold flex items-center text-slate-800">
            <User size={20} className="mr-3 text-slate-600" />
            Demographics
          </h2>
          <ChevronDown
            size={20}
            className={`transition-transform text-slate-600 ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
        {expanded && (
          <div className="mt-2">
            {isLoadingDemographics ? (
              <div className="bg-white p-4 rounded shadow text-center">
                <Loader size={24} className="inline-block animate-spin mr-2" />
                <span>Loading demographics data...</span>
              </div>
            ) : demographicsError ? (
              <div className="bg-white p-4 rounded shadow">
                <div className="p-4 border border-red-200 bg-red-50 text-red-700 rounded">
                  <p>{demographicsError}</p>
                  <button
                    onClick={loadDemographics}
                    className="mt-2 text-sm underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : (
              <FormPageLayout
                onBack={() => {}}
                hideBack
                actions={[
                  ...(canSaveDemographics ? [{
                    key: 'save',
                    label: 'Save Patient Data',
                    loadingLabel: 'Saving...',
                    onClick: handleDemographicsSubmit,
                    variant: 'primary',
                    loading: isSubmitting,
                    disabled: isSubmitting,
                  }] : []),
                ] as ActionButtonConfig[]}
                title="Demographics"
              >
                {/* Success Message Banner */}
                {successMessage && (
                  <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="bg-green-100 p-2 rounded-full mr-3">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-green-800">{successMessage}</p>
                      </div>
                      <button
                        onClick={() => setSuccessMessage('')}
                        className="text-green-600 hover:text-green-800"
                        aria-label="Dismiss"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Read-only mode notification */}
                {isReadOnlyView && (
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-sm text-gray-700">
                      {readOnly
                        ? 'This patient is discharged. Demographics are read-only.'
                        : 'You do not have permission to edit Demographics. Viewing in read-only mode.'}
                    </p>
                  </div>
                )}

                {/* 1. Patient Identification */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">1. Patient Identification</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        First Name
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        defaultValue={demographicsData?.firstName || ''}
                        disabled={isReadOnlyView}
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        defaultValue={demographicsData?.lastName || ''}
                        disabled={isReadOnlyView}
                      />
                    </div>
                    <div>
                      <label htmlFor="medicalRecordNumber" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Medical Record Number
                      </label>
                      <input
                        id="medicalRecordNumber"
                        type="text"
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={patientMRN || ''}
                        disabled={isReadOnlyView}
                      />
                    </div>
                    <div>
                      <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Date of Birth
                      </label>
                      <input
                        id="dob"
                        type="date"
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        defaultValue={demographicsData?.dateOfBirth || ''}
                        disabled={isReadOnlyView}
                      />
                    </div>
                    <div>
                      <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Gender
                      </label>
                      <input
                        id="gender"
                        type="text"
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.gender || ''}
                        disabled={isReadOnlyView}
                      />
                    </div>
                    <div>
                      <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Preferred Language <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="preferredLanguage"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.preferredLanguage || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            preferredLanguage: e.target.value
                          });
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="raceEthnicity" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Race/Ethnicity
                      </label>
                      <input
                        id="raceEthnicity"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.raceEthnicity || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            raceEthnicity: e.target.value
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 2. Guardians & legal representatives (PAT-02) */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">2. Guardians &amp; legal representatives</h2>
                  <GuardiansSection patientId={patientId} readOnly={isReadOnlyView} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label htmlFor="patientPhone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Patient Phone <span className="text-gray-500 text-xs">(optional, for older children)</span>
                      </label>
                      <input
                        id="patientPhone"
                        type="tel"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.phoneNumber || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            phoneNumber: e.target.value
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="patientEmail" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Patient Email <span className="text-gray-500 text-xs">(optional)</span>
                      </label>
                      <input
                        id="patientEmail"
                        type="email"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.email || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            email: e.target.value
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 3. Address */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">3. Address</h2>
                  {uspsAddressVerifyEnabled && addressVerification && (
                    <Banner
                      variant="warning"
                      className="mb-4"
                      title={addressVerification.status === 'NOT_FOUND' ? 'USPS could not find this address' : 'USPS suggests a different address'}
                      onDismiss={() => setAddressVerification(null)}
                    >
                      {addressVerification.suggestion ? (
                        <span>
                          Suggested: {[
                            addressVerification.suggestion.street1,
                            addressVerification.suggestion.street2,
                            addressVerification.suggestion.city,
                            addressVerification.suggestion.state,
                            addressVerification.suggestion.zipCode,
                          ].filter(Boolean).join(', ')}
                        </span>
                      ) : (
                        <span>Check the street, city and ZIP code. The address was saved as entered.</span>
                      )}
                    </Banner>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Street Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="streetAddress"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.address?.street1 || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            address: {
                              ...demographicsData?.address!,
                              street1: e.target.value
                            }
                          });
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="city"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.address?.city || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            address: {
                              ...demographicsData?.address!,
                              city: e.target.value
                            }
                          });
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        State <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="state"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.address?.state || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            address: {
                              ...demographicsData?.address!,
                              state: e.target.value
                            }
                          });
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Zip Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="zipCode"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.address?.zipCode || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            address: {
                              ...demographicsData?.address!,
                              zipCode: e.target.value
                            }
                          });
                        }}
                        required
                      />
                    </div>
                    {/* County of Residence */}
                    <div>
                      <label htmlFor="countyOfResidence" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        County of Residence
                      </label>
                      <select
                        id="countyOfResidence"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.countyOfResidence || ''}
                        onChange={(e) => setDemographicsData({ ...demographicsData!, countyOfResidence: e.target.value })}
                      >
                        <option value="">Select County</option>
                        {texasCountyOptions}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Required for CCP Prior Authorization</p>
                    </div>
                  </div>
                </div>

                {/* 4. Emergency Contact */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">4. Emergency Contact</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="emergencyContactName"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.emergencyContact?.name || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            emergencyContact: {
                              ...demographicsData?.emergencyContact!,
                              name: e.target.value
                            }
                          });
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="emergencyContactRelationship" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Relationship
                      </label>
                      <input
                        id="emergencyContactRelationship"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.emergencyContact?.relationship || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            emergencyContact: {
                              ...demographicsData?.emergencyContact!,
                              relationship: e.target.value
                            }
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="emergencyContactPhone"
                        type="tel"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.emergencyContact?.phoneNumber || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            emergencyContact: {
                              ...demographicsData?.emergencyContact!,
                              phoneNumber: e.target.value
                            }
                          });
                        }}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* 5. Primary Care Physician (PCP) */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">5. Primary Care Physician (PCP)</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="pcpName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="pcpName"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.pcpName || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            pcpName: e.target.value
                          });
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="pcpPhone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="pcpPhone"
                        type="tel"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.pcpPhone || ''}
                        onChange={(e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            pcpPhone: e.target.value
                          });
                        }}
                        required
                      />
                    </div>
                    {/* Date Physician Last Saw Client */}
                    <div>
                      <label htmlFor="datePhysicianLastSawClient" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Date Physician Last Saw Client
                      </label>
                      <input
                        id="datePhysicianLastSawClient"
                        type="date"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.datePhysicianLastSawClient || ''}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setDemographicsData({ ...demographicsData!, datePhysicianLastSawClient: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Required for CCP Prior Authorization</p>
                    </div>
                  </div>
                </div>

                {/* 6. Insurance Information */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">6. Insurance Information</h2>
                  {coverageOwnsIds && (
                    <p className="mb-3 text-sm text-slate-600 text-left" data-testid="coverage-owned-ids">
                      The Medicaid ID and Medicare ID come from Insurance and Coverage. Change them there.
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="medicaidId" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Medicaid ID <span className="text-gray-500 text-xs">(Texas, 9 digits)</span>
                      </label>
                      <input
                        id="medicaidId"
                        type="text"
                        disabled={isReadOnlyView || coverageOwnsIds}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView || coverageOwnsIds ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.insuranceInfo?.medicaidId || ''}
                        onChange={isReadOnlyView || coverageOwnsIds ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            insuranceInfo: {
                              ...demographicsData?.insuranceInfo!,
                              medicaidId: e.target.value
                            }
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="mcoName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        MCO Name
                      </label>
                      <input
                        id="mcoName"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.insuranceInfo?.mcoName || ''}
                        onChange={isReadOnlyView ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            insuranceInfo: {
                              ...demographicsData?.insuranceInfo!,
                              mcoName: e.target.value
                            }
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="mcoMemberId" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        MCO Member ID
                      </label>
                      <input
                        id="mcoMemberId"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.insuranceInfo?.mcoMemberId || ''}
                        onChange={isReadOnlyView ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            insuranceInfo: {
                              ...demographicsData?.insuranceInfo!,
                              mcoMemberId: e.target.value
                            }
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="medicareId" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Medicare ID <span className="text-gray-500 text-xs">(if dual eligible)</span>
                      </label>
                      <input
                        id="medicareId"
                        type="text"
                        disabled={isReadOnlyView || coverageOwnsIds}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView || coverageOwnsIds ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.insuranceInfo?.medicareId || ''}
                        onChange={isReadOnlyView || coverageOwnsIds ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            insuranceInfo: {
                              ...demographicsData?.insuranceInfo!,
                              medicareId: e.target.value
                            }
                          });
                        }}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={isPrimaryHolder}
                          onChange={(e) => setIsPrimaryHolder(e.target.checked)}
                          disabled={isReadOnlyView}
                          className={isReadOnlyView ? 'mr-2 cursor-not-allowed opacity-60' : 'mr-2'}
                        />
                        Patient is primary policyholder
                      </label>
                    </div>
                    {!isPrimaryHolder && (
                      <>
                        <div>
                          <label htmlFor="policyholderName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                            Policyholder Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="policyholderName"
                            type="text"
                            disabled={isReadOnlyView}
                            className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                            value={demographicsData?.insuranceInfo?.policyHolderName || ''}
                            onChange={isReadOnlyView ? undefined : (e) => {
                              setDemographicsData({
                                ...demographicsData!,
                                insuranceInfo: {
                                  ...demographicsData?.insuranceInfo!,
                                  policyHolderName: e.target.value
                                }
                              });
                            }}
                            required={!isPrimaryHolder}
                          />
                        </div>
                        <div>
                          <label htmlFor="policyholderDob" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                            Policyholder DOB <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="policyholderDob"
                            type="date"
                            disabled={isReadOnlyView}
                            className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                            value={demographicsData?.insuranceInfo?.policyHolderDob || ''}
                            onChange={isReadOnlyView ? undefined : (e) => {
                              setDemographicsData({
                                ...demographicsData!,
                                insuranceInfo: {
                                  ...demographicsData?.insuranceInfo!,
                                  policyHolderDob: e.target.value
                                }
                              });
                            }}
                            required={!isPrimaryHolder}
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <label htmlFor="primaryInsuranceProvider" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Primary Insurance Provider
                      </label>
                      <input
                        id="primaryInsuranceProvider"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.insuranceInfo?.provider || ''}
                        onChange={isReadOnlyView ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            insuranceInfo: {
                              ...demographicsData?.insuranceInfo!,
                              provider: e.target.value
                            }
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="primaryInsurancePolicyNumber" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Primary Insurance ID/Policy Number
                      </label>
                      <input
                        id="primaryInsurancePolicyNumber"
                        type="text"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.insuranceInfo?.policyNumber || ''}
                        onChange={isReadOnlyView ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            insuranceInfo: {
                              ...demographicsData?.insuranceInfo!,
                              policyNumber: e.target.value
                            }
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 7. Medical Summary (Brief) */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">7. Medical Summary (Brief)</h2>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="birthHistory" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Birth History <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="birthHistory"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded h-24 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.birthHistory || ''}
                        onChange={isReadOnlyView ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            birthHistory: e.target.value
                          });
                        }}
                        required
                      ></textarea>
                    </div>
                    <div>
                      <label htmlFor="medicalEquipments" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Medical Equipment
                      </label>
                      <textarea
                        id="medicalEquipments"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded h-24 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.medicalEquipments || ''}
                        onChange={isReadOnlyView ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            medicalEquipments: e.target.value
                          });
                        }}
                      ></textarea>
                    </div>
                    <div>
                      <label htmlFor="precautions" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Precautions <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="precautions"
                        disabled={isReadOnlyView}
                        className={`w-full p-2 border border-gray-300 rounded h-24 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        value={demographicsData?.precautions || ''}
                        onChange={isReadOnlyView ? undefined : (e) => {
                          setDemographicsData({
                            ...demographicsData!,
                            precautions: e.target.value
                          });
                        }}
                        required
                      ></textarea>
                    </div>

                    {/* Past Hospitalizations List */}
                    <div className="mt-4">
                      <GenericListView
                        title="Past Hospitalizations / Surgeries"
                        icon={<Calendar size={20} />}
                        expanded={hospitalizationsExpanded}
                        onToggleExpand={() => setHospitalizationsExpanded(!hospitalizationsExpanded)}
                        items={demographicsData?.pastHospitalizations || []}
                        keyExtractor={(hosp: any) => getHospitalizationData(hosp).id}
                        showNew={!isReadOnlyView}
                        columns={[
                          {
                            key: 'dates',
                            label: 'Dates',
                            width: 'w-64',
                            render: (hosp: any) => {
                              const { admitDate, dischargeDate, days } = getHospitalizationData(hosp);
                              return (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-slate-800">
                                    {formatDate(admitDate)} - {formatDate(dischargeDate)}
                                  </span>
                                  <span className="text-xs text-slate-500 bg-slate-100 rounded px-2 py-0.5">
                                    {days} day{days !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              );
                            },
                          },
                          {
                            key: 'description',
                            label: 'Description',
                            width: 'flex-1 min-w-0',
                            render: (hosp: any) => {
                              const { description } = getHospitalizationData(hosp);
                              return (
                                <div className="text-sm text-slate-700 truncate">
                                  {description || <span className="italic text-slate-400">No description</span>}
                                </div>
                              );
                            },
                          },
                        ]}
                        actions={[
                          {
                            icon: <Edit size={14} />,
                            label: 'Edit hospitalization',
                            onClick: (hosp: any) => openEditHospitalization(getHospitalizationData(hosp).id),
                            className: 'text-blue-600 hover:bg-blue-50',
                            disabled: isReadOnlyView,
                          },
                          {
                            icon: <Trash2 size={14} />,
                            label: 'Delete hospitalization',
                            onClick: (hosp: any) => setHospitalizationToDelete(getHospitalizationData(hosp).id),
                            className: 'text-red-600 hover:bg-red-50',
                            disabled: isSubmitting || isReadOnlyView,
                          },
                        ]}
                        mobileCard={{
                          primaryText: (hosp: any) => {
                            const { admitDate, dischargeDate } = getHospitalizationData(hosp);
                            return `${formatDate(admitDate)} - ${formatDate(dischargeDate)}`;
                          },
                          secondaryText: (hosp: any) => {
                            const { description } = getHospitalizationData(hosp);
                            return description || 'No description';
                          },
                          badges: (hosp: any) => {
                            const { days } = getHospitalizationData(hosp);
                            return [
                              {
                                label: `${days} day${days !== 1 ? 's' : ''}`,
                                className: 'bg-slate-100 text-slate-700',
                              },
                            ];
                          },
                        }}
                        loading={isLoadingDemographics}
                        newButtonLabel="Add Hospitalization"
                        onNewClick={isReadOnlyView ? undefined : openAddHospitalization}
                        emptyStateIcon={<Calendar size={48} />}
                        emptyStateTitle="No past hospitalizations"
                        emptyStateMessage="No past hospitalizations or surgeries recorded."
                      />
                      <HospitalizationDialog
                        open={!!hospitalizationDialog && !isReadOnlyView}
                        initial={hospitalizationDialog?.initial ?? null}
                        onCancel={() => setHospitalizationDialog(null)}
                        onSave={saveHospitalizationLocally}
                      />
                      <ConfirmDialog
                        open={!!hospitalizationToDelete}
                        title="Delete hospitalization"
                        message="Remove this hospitalization? The change is saved with “Save Patient Data”."
                        confirmLabel="Delete"
                        destructive
                        onConfirm={confirmDeleteHospitalization}
                        onCancel={() => setHospitalizationToDelete(null)}
                      />
                    </div>

                    {/* Submit button loading state */}
                    {isSubmitting && (
                      <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-10">
                        <div className="bg-white p-4 rounded shadow-lg">
                          <Loader size={24} className="inline-block animate-spin mr-2" />
                          <span>Processing...</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 8. Allergies (PAT-04) */}
                <div className="mb-8" id="demographics-allergies">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">
                    8. Allergies <span className="text-red-500">*</span>
                  </h2>
                  <AllergiesSection
                    allergies={allergyValue.allergies}
                    allergiesNoneKnown={allergyValue.allergiesNoneKnown}
                    readOnly={isReadOnlyView}
                    showError={showAllergyError}
                    onChange={(value) =>
                      setDemographicsData((prev) =>
                        prev ? { ...prev, allergies: value.allergies, allergiesNoneKnown: value.allergiesNoneKnown } : prev
                      )
                    }
                  />
                </div>

                {/* 9. Diagnoses (PAT-05) */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4 border-b pb-2 text-left">9. Diagnoses</h2>
                  <DiagnosisList patientId={patientId} readOnly={isReadOnlyView} />
                </div>

              </FormPageLayout>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Demographics;